<template>
	<div>
		<v-form ref="form" v-model="isValid">
			<div v-if="display.mobile.value && isAuthenticated" class="d-flex justify-center mb-2">
				<SaveStatusIndicator :status="saveStatus" :last-saved-at="lastSavedAt" />
			</div>
			<div class="d-flex justify-center">
				<v-btn
					v-if="display.mobile.value && !isViewOnly && !isExternalEditor && isAuthenticated"
					v-test="`${entityType}-save`"
					v-track="`${entityType}-save-button`"
					elevation="0"
					color="primary"
					rounded="pill"
					class="w-100 mb-4"
					size="large"
					:disabled="isSaving"
					@click="save()">
					Save
				</v-btn>
			</div>

			<!-- Name -->
			<CharacterLimitedTextField
				v-model.trim="entity.name"
				variant="outlined"
				:disabled="disableAutocompletes"
				label="Name (Required)"
				:max-length="255"
				:over-limit-message="'Name must be 255 characters or fewer.'"
				:rules="nameRules"
				@update:modelValue="emit('update:entity', entity)"></CharacterLimitedTextField>

			<!-- User (Conversation Only) -->
			<UserAutoComplete
				v-if="entityType === 'conversation'"
				v-model="selectedUser"
				label="User (Optional)"
				:disabled="disableUserField"
				:multiple="false"
				:closable-chips="true"
				:project-id="(selectedProject && selectedProject.id) || undefined"
				@update:model-value="emit('update:entity', entity)" />

			<!-- Status (Project Only) -->
			<EntityStatusSelect
				v-if="entityType === 'project'"
				label="Status (Required)"
				:disabled="disableAutocompletes"
				:entity-type="'project'"
				:model-value="selectedStatus || ''"
				@update:model-value="onProjectStatusInput" />

			<!-- Address (Project Only) -->
			<div v-if="entityType === 'project'">
				<GoogleMapsAutocomplete
					v-if="entityType === 'project'"
					id="map"
					ref="autocompleteRef"
					v-model="selectedAddress"
					:disabled="disableAutocompletes"
					menu-icon="hidden-chevron"
					v-test="'project-address'"
					label="Address (Optional)"
					placeholder="Start typing an address."
					clearable
					class="small-pill-input"
					@place-changed="onPlaceChanged">
					<template #append-inner>
						<v-icon
							v-test="'project-address-location-picker'"
							:disabled="disableAutocompletes"
							class="cursor-pointer"
							@click="openAddressLookupDialog()"
							>icon-map-pin-stroke</v-icon
						>
						<v-icon v-if="showMapsIcon" :disabled="!selectedAddress?.formattedAddress" @click="openMapsApp()" class="ml-1"
							>icon-myworld-stroke</v-icon
						>
					</template>
				</GoogleMapsAutocomplete>

				<!-- Fallback while Google Places is loading to avoid runtime errors -->
				<BaseTextField
					v-else
					v-model="address"
					v-test="'project-address'"
					variant="outlined"
					:disabled="true"
					label="Address (Optional)"
					placeholder="Start typing an address."
					:hide-details="true" />
			</div>

			<!-- Customer (Conversation / Project) Aka Contact -->
			<CustomerAutoComplete
				v-model="selectedCustomer"
				label="Contact (Optional)"
				:disabled="disableAutocompletes"
				:multiple="props.entityType === 'project'"
				:chips="props.entityType === 'project'"
				:closable-chips="true"
				placeholder="Start typing to search or create a new Contact."
				class="small-pill-input"
				@update:model-value="emit('update:entity', entity)"
				@create-customer="onCreateCustomer"
				@edit-customer="onEditCustomer" />

			<!-- Project (Conversation Only)-->
			<ProjectAutoComplete
				v-if="entityType === 'conversation'"
				v-model="selectedProject"
				label="Project (Optional)"
				:disabled="disableAutocompletes"
				:multiple="false"
				:entity="entity as Conversation"
				@update:model-value="emit('update:entity', entity)" />

			<!-- Keep the field neutral and inert until the server permissions arrive. -->
			<div :inert="permissionsLoading || undefined" :aria-busy="permissionsLoading || undefined">
				<v-progress-linear v-if="permissionsLoading" indeterminate color="primary" aria-label="Loading tag permissions" />
				<!-- Tags (Conversation / Project) -->
				<TagComboBox
					v-model="selectedTags"
					label="Tags (Optional)"
					:disabled="!permissionsLoading && disableTagEdits"
					:can-create="!permissionsLoading && !!props.isAdmin && !isExternalEditor"
					:multiple="true"
					placeholder="Add tags"
					class="small-pill-input"
					@focus="handleTagsFocus"
					@blur="handleTagsBlur"
					@update:model-value="emit('update:entity', entity)" />
			</div>

			<!-- Status (Conversation Only)-->
			<EntityStatusSelect
				v-if="showConversationStatus"
				label="Status (Required)"
				:disabled="disableAutocompletes"
				:entity-type="'conversation'"
				:model-value="selectedStatus || ''"
				@update:model-value="onConversationStatusInput" />

			<!-- Notes (Conversation) -->
			<AppTextarea
				v-if="entityType === 'conversation'"
				:model-value="selectedNotes"
				:disabled="disableAutocompletes"
				variant="outlined"
				placeholder="Type to add notes"
				hide-details="auto"
				:character-limit="NOTES_MAX_LENGTH"
				over-limit-message="Please enter a shorter note."
				@update:model-value="onNotesInput">
				<template #label><FieldLabel label="Notes (Optional)" /></template>
			</AppTextarea>

			<!-- Started By (Conversation, Self Service / Web Form) -->
			<BaseTextField
				v-if="isSelfServiceStartedBy"
				v-test="'entity-started-by'"
				variant="outlined"
				:disabled="true"
				v-model="selfServiceStartedByValue"
				label="Started By (Optional)" />

			<!-- Created By (Project Only) -->
			<BaseTextField
				v-if="entityType === 'project'"
				v-model="selectedCreatedBy"
				label="Created By (Optional)"
				variant="outlined"
				:disabled="true"
				@update:value="emit('update:entity', entity)" />

			<!-- Customer Review (Conversation / Project) — LSP-21829 (D15): below the About fields, above Additional Information. -->
			<EntityCustomerReviews
				v-if="customerReviewEntityType && entity?.id"
				:entity-type="customerReviewEntityType"
				:entity-id="entity.id" />

			<!-- Additional Information (Conversation / Project) -->
			<div v-if="hasAdditionalInfo" class="d-flex flex-column ga-2 mt-4">
				<div class="media-header d-flex align-center">
					<div v-test="'entity-additional-information-header'" class="heading">Additional Information</div>
					<v-btn
						v-test="'entity-additional-information-view-all'"
						icon="mdi-eye"
						variant="text"
						color="primary"
						size="small"
						class="ml-2"
						@click="showAdditionalInfoDialog = true" />
				</div>
				<template v-for="field in additionalFormFields" :key="field.id">
					<v-textarea
						v-test="`entity-additional-information-${field.FormField?.name}`"
						:label="field.FormField?.name"
						:model-value="field.value || '—'"
						rows="1"
						auto-grow
						no-resize
						readonly />
					<a
						v-if="addressMapLink(field)"
						v-test="'entity-additional-information-map-link'"
						:href="addressMapLink(field)!"
						:aria-label="`Open ${field.FormField?.name ?? 'address'} in Maps`"
						class="text-body-2 font-weight-medium"
						target="_blank"
						rel="noopener noreferrer"
						>Open in Maps</a
					>
				</template>
				<Dialog
					:dialog-visible="showAdditionalInfoDialog"
					title="Additional Information"
					:remove-agree-button="true"
					cancel-text="Close"
					@cancel="showAdditionalInfoDialog = false">
					<template #dialog-body>
						<div class="d-flex flex-column ga-3">
							<div v-for="field in additionalFormFields" :key="field.id" class="d-flex flex-column">
								<span class="text-body-2 font-weight-bold">{{ field.FormField?.name }}</span>
								<span class="text-body-2" style="white-space: pre-wrap; word-break: break-word; user-select: text">{{
									field.value || "—"
								}}</span>
								<a
									v-if="addressMapLink(field)"
									v-test="'entity-additional-information-map-link'"
									:href="addressMapLink(field)!"
									:aria-label="`Open ${field.FormField?.name ?? 'address'} in Maps`"
									class="text-body-2 font-weight-medium"
									target="_blank"
									rel="noopener noreferrer"
									>Open in Maps</a
								>
							</div>
						</div>
					</template>
				</Dialog>
			</div>

			<CreateCustomerDialog ref="CreateCustomerDialogRef"></CreateCustomerDialog>
			<AddressLookupDialog
				ref="addressLookupDialogRef"
				:initial-geo-address="selectedAddress"
				@geoAddressSelected="onGeoAddressSelected" />
			<Dialog
				:dialog-visible="showReassignConfirmDialog"
				title="Reassign this session"
				message="If you reassign this session, it will no longer appear on your My Stuff / Sessions page. Do you still want to reassign it?"
				agree-text="Reassign"
				cancel-text="Cancel"
				@agree="onReassignConfirm"
				@cancel="onReassignCancel" />
		</v-form>
	</div>
</template>
<script setup lang="ts">
	import { FIELDS_NEED_ATTENTION } from "@/helpers/userMessages";
	import UserAutoComplete from "@/components/formfields/UserAutoComplete.vue";
	import CustomerAutoComplete from "@/components/formfields/CustomerAutoComplete.vue";
	import ProjectAutoComplete from "@/components/formfields/ProjectAutoComplete.vue";
	import TagComboBox from "@/components/tags/ComboBox.vue";
	import EntityStatusSelect from "@/components/formfields/EntityStatusSelect.vue";
	import BaseTextField from "@/components/formfields/BaseTextField.vue";
	import GoogleMapsAutocomplete from "@/components/formfields/GoogleMapsAutocomplete.vue";
	import AddressLookupDialog from "@/components/location/AddressLookupDialog.vue";
	import SaveStatusIndicator from "@/components/common/entity/SaveStatusIndicator.vue";
	import EntityCustomerReviews from "@/components/common/entity/EntityCustomerReviews.vue";
	import { useAutosave, type SaveState } from "@/composables/useAutosave";
	import type { Conversation } from "@/types/Conversation";
	import type { EntityType } from "@/types/EntityType";
	import type { Project } from "@/types/Project";
	import type { User } from "@/types/User";
	import type { Label } from "@/types/Label";
	import type { Customer } from "@/types/Customer";
	import { ref, Ref, computed, getCurrentInstance, onMounted, onUnmounted, nextTick, watch } from "vue";
	import api from "@/api";
	import { calculateTimeout } from "@/helpers/utilities";
	import Dialog from "@/components/Dialog.vue";
	import CreateCustomerDialog from "@/components/CreateCustomerDialog.vue";
	import constants from "@/api/constants";
	import { clearAutocompletePlaceholder, isManualAddress, type GeoAddress } from "@/helpers/location";
	import { VuetifyValidation, type ValidatableFormRef } from "@/helpers/Validation";
	import type { FormSubmissionField } from "@/types/forms/FormSubmissionField";
	import { FieldType } from "@/types/forms/FieldType";

	import { useDisplay } from "vuetify";
	import { useToast } from "vue-toastification";
	import DeviceHelper from "@/helpers/device";
	import FieldLabel from "@common-ui/components/FieldLabel.vue";
	import { AppTextarea } from "@common-ui/components";
	import { countCodePoints } from "@common-ui/utilities/characterCount";

	// Max height for menus on mobile devices. Should fit 3 items at once.
	const MOBILE_MENU_MAX_HEIGHT = "160px";

	const instance = getCurrentInstance();
	const display = useDisplay();
	const toast = useToast();

	interface Props {
		entity: Conversation | Project;
		entityType: EntityType;
		isViewOnly?: boolean;
		isAdmin?: boolean;
		currentUserId?: string | null;
		hasFreeTrialEnded?: boolean;
		isScheduled?: boolean;
		isDirty?: boolean;
		isValid?: boolean;
		isSameOrganization?: boolean;
		permissionsLoading?: boolean;
		// LSP-17772: whether the viewer is an external share editor (share origin + different org +
		// not view-only). Resolved by the parent via isExternalShareEditor (src/helpers/projectAccess.ts)
		// — the single source of truth — instead of a local org-only approximation.
		isExternalEditor?: boolean;
		isMobile?: boolean;
	}

	const props = withDefaults(defineProps<Props>(), {
		isMobile: false,
		isExternalEditor: false,
		currentUserId: null
	});

	// LSP-21829: the Customer Review section loads by session or by project; other entity types have none.
	const customerReviewEntityType = computed<"session" | "project" | null>(() => {
		if (props.entityType === "conversation") return "session";
		if (props.entityType === "project") return "project";
		return null;
	});

	// LSP-17482: the project-only server-unit backstop rule that used to live here is gone —
	// contact-api's updateProject now counts CODE POINTS too (projects.ts), matching this field's own
	// :max-length rule — so nameRules no longer varies by entityType.
	const nameRules = computed(() => {
		return [VuetifyValidation.RequiredValidationField("Name is required.").isValid];
	});

	const emit = defineEmits<{
		(e: "save", entity: Conversation | Project): void;
		(e: "update:entity", entity: Conversation | Project): void;
		(e: "update:isDirty", value: boolean): void;
		(e: "update:isValid", value: boolean): void;
		(e: "hide-lucky"): void;
		(e: "show-lucky"): void;
		(e: "reassigned-away"): void;
		(e: "update:saveState", value: SaveState): void;
	}>();

	// break-it round 2: the template ref to the v-form, used solely to force a re-validate when
	// entityType changes — see the watcher below nameRules depends on.
	const form = ref<ValidatableFormRef | null>(null);

	/* Them Properties */
	const isValid: Ref<boolean> = ref(false);
	const isSaving: Ref<boolean> = ref(false); // Controls if the component is currently saving.
	const isDirty: Ref<boolean> = ref(props.isDirty ?? false); // Controls if the component is currently dirty.
	const selectedCustomer: Ref<Customer[] | Customer | null> =
		props.entityType === "project"
			? ref(((props.entity as any)?.Customers as Customer[]) || [])
			: ref(((props.entity as any)?.Customers?.[0] as Customer) || null); // Controls the selected customer(s).
	const selectedTags: Ref<Label[]> = ref((props.entity as Conversation | Project)?.Labels || []); // Controls the selected tags.
	// Conversation Only Props
	const selectedUser: Ref<User | null> = ref(((props.entity as any)?.User as User) || null); // Controls the selected team member.
	const selectedProject: Ref<Project | null> =
		props.entityType === "conversation" ? ref(((props.entity as any)?.Project as Project) || null) : ref(null); // Controls the selected project(s).
	const selectedNotes: Ref<string> = ref((props.entity as Conversation)?.notes || ""); // Controls the selected notes.
	// LSP-16701 (Bugbot 3808760073 — data loss): the last notes value this form is known to be in sync
	// with. It decides whether save() SENDS `notes` at all. Deliberately additive: `notes` stays inside
	// the joined `initialSignature`, so overall dirty semantics (and the LSP-16361 / LSP-16626
	// invariants) are untouched — this ref is only consulted when building the save payload.
	const baselineNotes: Ref<string> = ref((props.entity as Conversation)?.notes || "");
	// A server-pushed notes value the form REFUSED to adopt because the user had unsaved edits. Held so
	// a save that overwrites it can say so out loud instead of destroying it silently.
	const pendingServerNotes: Ref<string | null> = ref(null);
	// Break-it rounds 1-2 (defect 4): which entity the held note above was pushed FOR. A note stashed while
	// the form was dirty belongs to the entity that was on screen when it arrived, so it must only ever be
	// adopted — or reported as a conflict — against that same entity. Otherwise the outgoing conversation's
	// note is misattributed onto the incoming one, which is worse than the silent discard the adoption
	// replaced: it shows, and on the next save PERSISTS, one conversation's note under another.
	//
	// Both the id and the entity OBJECT are recorded. The id is the meaningful identity, but two DIFFERENT
	// entity objects can both be missing an id, and comparing `undefined` to `undefined` matched — letting
	// exactly the misattribution above through (round 2). The object reference disambiguates that case.
	let pendingServerNotesEntityId: string | number | null = null;
	let pendingServerNotesEntity: object | null = null;

	// Whether the held note belongs to the entity currently bound to this component. Every consumer of
	// pendingServerNotes must ask this first: adopting, rebaselining, or raising a conflict toast for a note
	// that belongs to a different entity is a correctness bug in each case.
	function heldNotesBelongToCurrentEntity(): boolean {
		const currentEntity = (props.entity as object | undefined) ?? null;
		const currentEntityId = (props.entity as { id?: string | number })?.id ?? null;
		if (pendingServerNotesEntityId !== null && currentEntityId !== null) {
			return pendingServerNotesEntityId === currentEntityId;
		}
		return pendingServerNotesEntity !== null && pendingServerNotesEntity === currentEntity;
	}
	// This admin path (save() -> api.updateConversation -> PUT /conversations/:id ->
	// ConversationsController.updateConversation) does NOT enforce a notes length limit server-side —
	// unlike contact-live's updateConversationNotesInsecure route, which does check
	// `Array.from(body.notes).length > 2048`. The only backstop here is the Postgres
	// VARCHAR(2048) column itself: an over-limit value that somehow reached the server would fail
	// with a 22001 (string data right truncation) -> generic 500 toast, not a friendly validation
	// error. AppTextarea's app-wide code-point counting makes this 2048 client-side rule the real
	// gate in practice (defense-in-depth only past that point). Per LSP-17265, over-limit input is
	// warned via the counter + rule turning red — never hard-truncated — so typing is never
	// silently swallowed.
	const NOTES_MAX_LENGTH = 2048;
	// Stores the raw AppTextarea value (no truncation) and keeps entity.notes / update:entity
	// in sync. The over-limit rule (bound on AppTextarea) is what blocks save/autosave via isValid.
	function onNotesInput(value: string): void {
		selectedNotes.value = value ?? "";
		(props.entity as Conversation).notes = selectedNotes.value as any;
		emit("update:entity", props.entity);
	}
	const selectedStatus: Ref<string> = ref(((props.entity as Conversation | Project)?.status as string) || "");
	const selectedAddress: Ref<GeoAddress | null> = ref(((props.entity as any)?.GeoAddress as GeoAddress) || null);
	// Project Only Props
	const selectedCreatedBy: Ref<string> = ref((props.entity as Project)?.User?.name || ""); // Controls the selected created by.
	// Disable inputs based on provided flags
	const showMapsIcon = computed(() => (DeviceHelper.isMobile || DeviceHelper.isTablet) && !isManualAddress(selectedAddress.value));
	// LSP-17772: the WHOLE About tab is read-only for an external editor — a share-origin collaborator
	// from a different org. The parent resolves this via isExternalShareEditor
	// (src/helpers/projectAccess.ts) and passes it down as isExternalEditor. The server enforces the
	// same restriction on updateProject.
	const disableAutocompletes = computed<boolean>(() => {
		const base = !!(isSaving.value || props.isViewOnly || props.hasFreeTrialEnded) || !!props.isExternalEditor;
		// For conversations, scheduled state should NOT disable general fields
		return props.entityType === "conversation" ? base : base || !!props.isScheduled;
	});

	function getCurrentAssigneeId(entity: any): string | null {
		return (entity?.User?.id ?? entity?.forcedUserId) || null;
	}

	// Resolve the assignee the picker currently represents. If selectedUser is null because the
	// entity arrived with forcedUserId but no populated User relationship (the picker never
	// showed the assignment), treat that as "no change" rather than "user cleared the picker"
	// so an unrelated save doesn't accidentally blank the assignee or trigger the reassign-away
	// dialog. If the user actively cleared a previously populated picker, selectedUser is null
	// and entity.User was non-null at mount — that path still returns null and behaves as a clear.
	function getNewAssigneeId(): string | null {
		if (selectedUser.value) {
			return (selectedUser.value as User).id ?? null;
		}

		const entity = props.entity as any;
		if (!entity?.User && entity?.forcedUserId) {
			return entity.forcedUserId as string;
		}

		return null;
	}

	// Dedicated disabled state for the user selector.
	// LSP-15744: admins can always edit; for non-admins on conversations, allow editing
	// only when the user is the current assignee (so they can reassign their own session).
	const disableUserField = computed<boolean>(() => {
		const base = !!(isSaving.value || props.isViewOnly || props.hasFreeTrialEnded) || !!props.isExternalEditor;
		if (base) {
			return true;
		}

		if (props.entityType !== "conversation") {
			return !props.isAdmin;
		}

		if (props.isAdmin) {
			return false;
		}

		const currentAssigneeId = getCurrentAssigneeId(props.entity);
		const isCurrentAssignee = !!props.currentUserId && props.currentUserId === currentAssigneeId;
		return !isCurrentAssignee;
	});

	// Disable tag edits if cross-organization on projects
	const disableTagEdits = computed<boolean>(() => {
		const baseDisabled = disableAutocompletes.value;
		if (props.entityType !== "project") return baseDisabled;
		if (props.isSameOrganization === false) return true;
		return baseDisabled;
	});

	/* Functions */
	const CreateCustomerDialogRef: Ref<any> = ref(null);
	const addressLookupDialogRef: Ref<any> = ref(null);
	const autocompleteRef: Ref<any> = ref(null);

	/**
	 * LSP-18456: the address-pin click used to be `addressLookupDialogRef?.open?.()` inline. Both
	 * optional chains swallow a miss, so a missing ref or a renamed `open` produced a dead icon with
	 * no signal anywhere — indistinguishable from the empty-dialog bug this ticket is really about,
	 * and one of the conditions that had to be ruled out by hand while diagnosing it. Log the fail
	 * condition (root CLAUDE.md, "Diagnostic logging when fixing silent failures") so the next
	 * "clicking the pin does nothing" report is a log lookup rather than a reconstruction.
	 */
	function openAddressLookupDialog() {
		if (typeof addressLookupDialogRef.value?.open !== "function") {
			console.warn("EntityAbout.openAddressLookupDialog: address lookup dialog is unavailable — the pin click did nothing", {
				entityType: props.entityType,
				entityId: (props.entity as { id?: string })?.id,
				hasDialogRef: !!addressLookupDialogRef.value,
				hasAddress: !!selectedAddress.value
			});
			return;
		}
		addressLookupDialogRef.value.open();
	}
	// const address: Ref<string> = ref(((props.entity as any)?.GeoAddress?.formattedAddress as string) || "");
	const isAuthenticated: Ref<boolean> = ref(true);
	const googlePlacesReady: Ref<boolean> = ref(false);
	const selfServiceStartedByValue: Ref<string> = ref("Self Service");
	const showAdditionalInfoDialog: Ref<boolean> = ref(false);

	const initialPlaceId = computed(() => {
		return ((props.entity as any)?.GeoAddress?.placeId as string) || undefined;
	});

	const isTagsComboboxFocused: Ref<boolean> = ref(false);

	const showReassignConfirmDialog: Ref<boolean> = ref(false);
	let reassignConfirmResolver: ((value: boolean) => void) | null = null;

	let intervalId: any = null;
	let stopAutocompletePolling: (() => void) | undefined;

	// LSP-16420 autosave (delay reduced to 3s in LSP-16953): 3s after the user stops editing, persist without a success toast and
	// surface a "Last saved …" indicator (failures still toast the error). The manual Save button is
	// kept and still shows its success toast.
	const {
		saveStatus,
		lastSavedAt,
		schedule: scheduleAutosave,
		cancel: cancelAutosave,
		reset: resetAutosave,
		markSaving,
		markSaved,
		markError
	} = useAutosave({
		performAutosave: () => save({ isAutosave: true })
	});
	// Single-flight guard shared by manual + auto saves so we never stack two PUTs on one entity.
	let isPersisting = false;
	// Resolves when the in-flight save fully settles. A save() that arrives mid-flight awaits this, then
	// runs itself — so its returned promise reflects a real persist (callers like promptSaveBeforeLeave
	// await save() before clearing isDirty / navigating).
	let activeSavePromise: Promise<void> | null = null;
	// Set once this instance unmounts (e.g. navigation swaps EntityAbout for the next entity). A save
	// whose PUT settles after unmount must not arm a stray autosave timer / fire a follow-up PUT.
	let isUnmounted = false;

	// Last reported reason autosave stood down, so the diagnostic below logs on reason CHANGES rather
	// than on every keystroke. null means "not currently standing down". break-it review (Bugbot).
	let lastAutosaveStandDownReason: string | null = null;

	// Schedule an autosave when there are genuine unsaved edits and saving is allowed; otherwise stand down.
	// Takes the already-computed dirty flag so the field watcher doesn't rebuild the dirty signature twice.
	function maybeScheduleAutosave(dirty: boolean): void {
		const allowed = dirty && isValid.value && !props.isViewOnly && !props.hasFreeTrialEnded && isAuthenticated.value;
		if (!allowed) {
			// Name WHICH of the five conditions stood autosave down. This PR added a new caller (the
			// watch(isValid, ...) that re-arms autosave once an over-limit value comes back under the
			// limit), so this branch is now reachable in more ways — and "autosave never fired" is exactly
			// the silent failure the per-condition logging in save() exists to make triageable.
			//
			// Logged once per REASON CHANGE, not once per call. This fires from the tracked-field watcher,
			// and warn-don't-cap (LSP-16465) means typing past the limit is normal behaviour — so a
			// per-call warn is one log PER KEYSTROKE for as long as the user keeps typing over the limit.
			// console.warn is forwarded to Datadog at full session sample rate (plugins/datadog.ts
			// forwardConsoleLogs), so that is a real log-volume problem, not just console noise (Bugbot).
			// Keying on the reason signature keeps the triage value — entering the state logs once, and a
			// DIFFERENT reason (e.g. the session goes view-only) logs again — without the flood.
			if (dirty) {
				const standDownReason = [
					isValid.value ? "" : "invalid",
					props.isViewOnly ? "viewOnly" : "",
					props.hasFreeTrialEnded ? "trialEnded" : "",
					isAuthenticated.value ? "" : "unauthenticated"
				]
					.filter(Boolean)
					.join(",");
				if (standDownReason !== lastAutosaveStandDownReason) {
					lastAutosaveStandDownReason = standDownReason;
					console.warn("EntityAbout.maybeScheduleAutosave: dirty but autosave not allowed — standing down", {
						entityId: (props.entity as { id?: string | number })?.id,
						entityType: props.entityType,
						reason: standDownReason,
						isValid: isValid.value,
						isViewOnly: props.isViewOnly === true,
						hasFreeTrialEnded: props.hasFreeTrialEnded === true,
						isAuthenticated: isAuthenticated.value
					});
				}
			}
			cancelAutosave();
			return;
		}
		// Autosave is allowed again — clear the latch so the NEXT stand-down (even for the same reason)
		// is reported. Without this, a user who goes over the limit, comes back under, then goes over
		// again would silently skip the second warning.
		lastAutosaveStandDownReason = null;
		if (isPersisting) {
			// An edit landed mid-PUT. Arming a timer is futile — the in-flight save's resetDirtyBaseline()
			// cancels it and rebaselines the form as clean. The in-flight save's own finally detects the
			// changed field values (signature compare) and reruns, so this edit still persists.
			return;
		}
		scheduleAutosave();
	}

	watch([saveStatus, lastSavedAt], () => {
		emit("update:saveState", { status: saveStatus.value, lastSavedAt: lastSavedAt.value });
	});

	function isReassigningAwayFromSelf(): boolean {
		if (props.entityType !== "conversation") {
			return false;
		}

		if (props.isAdmin) {
			return false;
		}

		if (!props.currentUserId) {
			return false;
		}

		const previousAssigneeId = getCurrentAssigneeId(props.entity);
		const newAssigneeId = getNewAssigneeId();
		return previousAssigneeId === props.currentUserId && newAssigneeId !== props.currentUserId;
	}

	function awaitReassignConfirmation(): Promise<boolean> {
		showReassignConfirmDialog.value = true;
		return new Promise<boolean>((resolve) => {
			reassignConfirmResolver = resolve;
		});
	}

	function onReassignConfirm() {
		showReassignConfirmDialog.value = false;
		const resolver = reassignConfirmResolver;
		reassignConfirmResolver = null;
		resolver?.(true);
	}

	function onReassignCancel() {
		showReassignConfirmDialog.value = false;
		const resolver = reassignConfirmResolver;
		reassignConfirmResolver = null;
		resolver?.(false);
	}

	function handleTagsFocus() {
		isTagsComboboxFocused.value = true;
		if (display.mdAndDown.value && DeviceHelper.isLandscape) {
			emit("hide-lucky");
		}
	}

	function handleTagsBlur() {
		isTagsComboboxFocused.value = false;
		if (display.mdAndDown.value && DeviceHelper.isLandscape) {
			emit("show-lucky");
		}
	}

	function handleWindowResize() {
		if (display.mdAndDown.value) {
			if (!DeviceHelper.isLandscape) {
				emit("show-lucky");
			} else if (DeviceHelper.isLandscape && isTagsComboboxFocused.value) {
				emit("hide-lucky");
			}
		}
	}

	onMounted(async () => {
		stopAutocompletePolling = clearAutocompletePlaceholder();
		try {
			const auth = (getCurrentInstance()?.proxy as any)?.$auth;
			if (typeof auth?.isAuthenticated === "function") {
				isAuthenticated.value = await auth.isAuthenticated();
			} else {
				isAuthenticated.value = false;
			}
		} catch (err) {
			isAuthenticated.value = false;
		}

		// Poll until Google Places is ready to avoid mounted hook errors in the autocomplete component
		const checkReady = () => {
			try {
				const g = (window as any)?.google;
				if (g?.maps?.places?.Autocomplete) {
					googlePlacesReady.value = true;
					if (intervalId) {
						clearInterval(intervalId);
						intervalId = null as any;
					}
				}
			} catch (_) {
				// ignore
			}
		};

		checkReady();
		if (!googlePlacesReady.value) {
			intervalId = setInterval(checkReady, 150);
		}
		window.addEventListener("resize", handleWindowResize);
	});

	onUnmounted(() => {
		isUnmounted = true;
		stopAutocompletePolling?.();
		if (intervalId) {
			clearInterval(intervalId);
			intervalId = null as any;
		}
		window.removeEventListener("resize", handleWindowResize);
	});

	// Keep local selectedStatus in sync with upstream prop changes
	watch(
		() => (props.entity as Conversation | Project)?.status as string | undefined,
		(newStatus) => {
			selectedStatus.value = newStatus || "";
		}
	);

	// LSP-16701: adopt a notes value pushed from the server (contact-live saved notes during a call →
	// Conversations UPDATE webhook → concierge-socket `conversation_update` → EntityDetailPage merges it
	// into the entity prop) so the Notes field updates live instead of only after a reload.
	//
	// selectedNotes is otherwise seeded ONCE at setup, which is why in-call notes never appeared here.
	// Adoption is deliberately conservative: it only applies while the form is entirely pristine, so it
	// can never overwrite text the user is composing. If ANY field is dirty we keep the local buffer and
	// log it — the incoming value still lands on the next entity load. (Whole-form rather than
	// notes-only dirtiness because notes is baked into the single joined `initialSignature`; keeping it
	// there avoids re-cutting the dirty-tracking invariants LSP-16361 / LSP-16626 stabilised.)
	watch(
		() => (props.entity as Conversation)?.notes as string | null | undefined,
		(incomingNotes) => {
			if (props.entityType !== "conversation") {
				return;
			}
			// Break-it round 4: an UNBOUND entity is not a server push. EntityDetailPage sets `entity` to
			// undefined transiently while it reloads (:2496), which surfaced here as `incoming === ""` — and
			// on a clean form that was ADOPTED, blanking the field and clearing a note held for the entity
			// being reloaded, so the note was gone before the entity came back to claim it.
			if (!props.entity) {
				console.log("EntityAbout: ignoring a notes watcher fire with no entity bound", {
					heldNotes: pendingServerNotes.value !== null
				});
				return;
			}

			const incoming = incomingNotes ?? "";
			// Echo guard: onNotesInput mutates props.entity.notes IN PLACE, so every keystroke re-triggers
			// this watcher with the user's own text. That is not a server push — compare against the live
			// buffer here (and ONLY here) precisely because the question is "is this just our own value
			// coming back?".
			if (incoming === selectedNotes.value) {
				return;
			}

			if (isFormDirty()) {
				// Review 4975055752 finding #1: only a value that actually moved off the last server-synced
				// baseline is a real push worth holding. Because onNotesInput mutates props.entity.notes in
				// place (the same object EntityDetailPage holds), EntityDetailPage.onConversationUpdate
				// compares each payload against that already-typed-into buffer and therefore re-writes the
				// UNCHANGED server value whenever the admin has typed — which arrives here looking exactly
				// like a push. Holding it made an ordinary unrelated `conversation_update` produce a bogus
				// "these notes also changed during the live call" toast on the next save, and poisoned the
				// diagnostic below that exists for the real conflict.
				// Break-it round 5, OPEN FINDING left to the deferred cross-writer ticket: a note held from an
				// EARLIER push is NOT retired here, so if the server later returns to this baseline the stale
				// intermediate can still be adopted or reported as a conflict. Retiring it needs "is this
				// payload the server's real current value, or an unchanged re-emission?" — and the two are
				// indistinguishable from here, since the watcher re-fires on an entity-object replacement
				// carrying the same value. Both attempted discriminators regressed legitimate hold cases
				// (same-id socket merge, project<->conversation cycle, transient unbind), so this is left for
				// the ticket that owns cross-writer notes conflict semantics rather than guessed at.
				if (incoming === baselineNotes.value) {
					return;
				}
				// Do NOT adopt — that would clobber text the user is composing. Remember the value instead:
				// save() uses it to decide whether it may omit `notes` from the payload (so the pushed note
				// survives) and, when the user really did edit notes, to warn that it is overwriting it.
				pendingServerNotes.value = incoming;
				pendingServerNotesEntityId = (props.entity as { id?: string | number })?.id ?? null;
				pendingServerNotesEntity = (props.entity as object | undefined) ?? null;
				console.warn("EntityAbout: server notes update held (not adopted) while the About form has unsaved edits", {
					conversationId: (props.entity as { id?: string })?.id,
					incomingLength: incoming.length,
					localLength: selectedNotes.value.length
				});
				return;
			}

			console.log("EntityAbout: adopting server notes update", {
				conversationId: (props.entity as { id?: string })?.id,
				incomingLength: incoming.length
			});
			selectedNotes.value = incoming;
			baselineNotes.value = incoming;
			pendingServerNotes.value = null;
			// A server-pushed value is not a user edit: re-cut the clean baseline in the same tick so the
			// tracked-field watcher sees a pristine form, never marks it dirty, and never arms an autosave
			// (same intent as acceptProgrammaticStatus for status).
			captureBaselineSignature();
			emitDirty(false);
		}
	);

	// LSP-16701 (found by the mid-save race test): adoption is CHANGE-triggered, so a value held while the
	// form was dirty would otherwise never reach the field once the form goes clean again (after a save, or
	// after the user reverts their edits) — it would sit unadopted until the next push or a reload. Retry
	// the held value at every point the form becomes clean.
	function tryAdoptPendingServerNotes(): void {
		const pending = pendingServerNotes.value;
		if (pending === null || props.entityType !== "conversation") {
			return;
		}
		// Break-it round 2: this function is reached from commitSavedBaseline and acceptProgrammaticStatus as
		// well as the tracked-field watcher, and it checked only dirtiness — so a note held for a PREVIOUS
		// entity was adopted onto whatever entity happened to be bound when the form next went clean. Same
		// invariant as adoptHeldNotesForEntityChange; asked here too because these callers do not run an
		// entity change.
		if (!heldNotesBelongToCurrentEntity()) {
			console.warn("EntityAbout: not adopting a held notes value — it belongs to a different entity", {
				heldForEntityId: pendingServerNotesEntityId,
				currentEntityId: (props.entity as { id?: string | number })?.id ?? null,
				pendingLength: pending.length
			});
			// Drop it rather than leaving it parked: bound to a different entity it can never legitimately
			// be adopted or reported here, and leaving it in place kept feeding the conflict checks in
			// save() a value from another conversation (break-it round 2). Only when an entity IS bound —
			// with none bound we cannot yet tell whose note this is, so it is left for the next check.
			if (props.entity) {
				pendingServerNotes.value = null;
				pendingServerNotesEntityId = null;
				pendingServerNotesEntity = null;
			}
			return;
		}
		if (isFormDirty()) {
			// Silent-failure diagnostic (review 4975055752, medium): this is the recovery path for this
			// ticket's core symptom, so a no-op here is exactly what a "the note never appeared" report
			// looks like. Say why it stood down, like the watcher's sibling gate does.
			console.log("EntityAbout: held server notes still not adopted — the About form is dirty", {
				conversationId: (props.entity as { id?: string })?.id,
				pendingLength: pending.length
			});
			return;
		}
		pendingServerNotes.value = null;
		if (pending === selectedNotes.value) {
			baselineNotes.value = pending;
			return;
		}
		console.log("EntityAbout: adopting held server notes now that the form is clean", {
			conversationId: (props.entity as { id?: string })?.id,
			incomingLength: pending.length
		});
		selectedNotes.value = pending;
		// Re-sync the entity prop to the adopted value. onNotesInput mutates props.entity.notes IN PLACE on
		// every keystroke, so on the REVERT path (review 5346441261 finding #3) the prop still holds the
		// value the user reverted TO while the held note lands only in selectedNotes. The prop watcher then
		// sees entity.notes differ from the buffer on a now-clean form and "adopts" that stale value right
		// back over the note — silently discarding the pushed note and clearing pendingServerNotes, so
		// nothing could ever recover it. Writing the prop here keeps the two in sync, so the watcher's echo
		// guard matches and there is no stale value left to re-adopt. No update:entity emit is needed: this
		// restores a value the parent itself pushed down (it holds this same object), rather than
		// introducing a new one.
		(props.entity as Conversation).notes = pending as any;
		baselineNotes.value = pending;
		captureBaselineSignature();
		emitDirty(false);
	}

	// Dirty tracking baseline and signature. status is tracked separately from the rest so a programmatic
	// status flip can be accepted as clean WITHOUT discarding genuine user edits to other fields (LSP-16361).
	const initialSignature: Ref<string> = ref("");
	const baselineStatus: Ref<string> = ref("");
	/** Cut a fresh clean baseline. */
	function captureBaselineSignature(): void {
		initialSignature.value = buildDirtySignature();
	}
	function buildDirtySignature(): string {
		const entityName = ((props.entity as { name?: string })?.name ?? "").toString();
		const userId = (selectedUser.value as User | null)?.id ?? "";
		const projectId = (selectedProject.value as Project | null)?.id ?? "";
		const customerSig = Array.isArray(selectedCustomer.value)
			? (selectedCustomer.value as Customer[])
					.map((c) => c.id)
					.sort()
					.join(",")
			: ((selectedCustomer.value as Customer | null)?.id ?? "");
		const tagsSig = [...(selectedTags.value || [])]
			.map((t) => t.name)
			.sort()
			.join(",");
		const notes = selectedNotes.value || "";
		// Optional-chained on `entity` itself (review 4977618875 finding #3, found by the entity-switch
		// test): a real switch blanks `entity` to undefined before loading the incoming one
		// (EntityDetailPage.vue:2496), and this PR's notes-adoption watcher calls isFormDirty() on that
		// interlude — so an unguarded deref threw a TypeError out of the watcher on EVERY entity switch,
		// aborting the callback (Vue swallows it into its error handler). Every other field above already
		// tolerates a missing entity.
		const addr = (props.entity as any)?.GeoAddress?.formattedAddress || "";
		return [entityName, userId, projectId, customerSig, tagsSig, notes, addr].join("|");
	}

	function isFormDirty(): boolean {
		return buildDirtySignature() !== initialSignature.value || (selectedStatus.value || "") !== baselineStatus.value;
	}

	function emitDirty(dirty: boolean): void {
		if (dirty !== isDirty.value) {
			isDirty.value = dirty;
			emit("update:isDirty", dirty);
		}
	}

	// Capture all current field values as the clean baseline (entity change).
	//
	// Review 4977618875 finding #3: this ran BEFORE adopting, and nulled pendingServerNotes
	// unconditionally and silently — which stranded the INCOMING entity's own notes on every real entity
	// switch. Its only caller is the id watcher below, so "resetDirtyBaseline ran" means "the entity
	// changed", and any note held in this same flush belongs to the entity we are switching TO:
	// EntityDetailPage.vue:2398 replaces `entity.value` by object REFERENCE, so id + status + notes all
	// change in one reactive flush and Vue runs the watchers in registration order — status-sync, then
	// notes-adoption, then this one. By the time the notes watcher runs, selectedStatus has already moved
	// to the incoming status while baselineStatus still holds the outgoing one, so isFormDirty() reads
	// true with ZERO user edits and the incoming notes are STASHED rather than adopted (the same
	// registration-order hazard acceptProgrammaticStatus documents). This function then threw the stash
	// away, so selectedNotes — seeded once at setup and never re-seeded on a switch — kept rendering the
	// PREVIOUS conversation's text under the new one: this ticket's original symptom via a new path. It
	// also left the outgoing conversation's unsaved text in the buffer under the incoming conversation,
	// where the next save would have persisted it onto the wrong row.
	function resetDirtyBaseline(): void {
		const heldNotesResolved = adoptHeldNotesForEntityChange();
		captureBaselineSignature();
		baselineStatus.value = selectedStatus.value || "";
		baselineNotes.value = selectedNotes.value || "";
		// Break-it round 3: cleared only once the held note's fate is actually DECIDED. This used to null it
		// unconditionally, which threw away a note still held for the CURRENT entity whenever the id watcher
		// fired with no entity bound — the transient `entity.value = undefined` that EntityDetailPage sets
		// while reloading (:2496). The entity coming back with the same id must still be able to adopt it.
		if (heldNotesResolved) {
			pendingServerNotes.value = null;
			pendingServerNotesEntityId = null;
			pendingServerNotesEntity = null;
		}
		isDirty.value = false;
		emit("update:isDirty", false);
		// Nothing left to persist — drop any pending autosave so a stale timer can't fire.
		cancelAutosave();
	}

	// Take a note held during an entity change, so the baselines below are cut from the INCOMING entity's
	// value rather than the outgoing one. Deliberately unconditional on dirtiness (unlike
	// tryAdoptPendingServerNotes): across an entity change there is no local edit worth protecting — the
	// buffer belongs to the entity we are leaving, and EntityDetailPage flushes that entity's pending
	// autosave before it swaps the prop. Every exit here logs, per the repo's silent-failure rule: a
	// no-op in this function looks exactly like a "the new session showed the old session's notes" report.
	// Returns whether the held note's fate was decided (adopted, or dropped as belonging elsewhere). False
	// means "undecidable right now" — the caller must LEAVE the note held rather than clearing it.
	function adoptHeldNotesForEntityChange(): boolean {
		const pending = pendingServerNotes.value;
		if (pending === null) {
			return true;
		}
		if (!props.entity) {
			// No entity bound: this is EntityDetailPage's transient unbind while it reloads (:2496), not a
			// switch to a different entity. We cannot yet tell whose note this is, so keep holding it.
			console.log("EntityAbout: entity change with no entity bound — keeping the held notes value", {
				heldForEntityId: pendingServerNotesEntityId,
				pendingLength: pending.length
			});
			return false;
		}
		if (props.entityType !== "conversation") {
			console.warn("EntityAbout: dropping a held notes value on an entity change — notes adoption is conversation-only", {
				entityId: (props.entity as { id?: string })?.id,
				entityType: props.entityType,
				pendingLength: pending.length
			});
			return true;
		}
		const incomingEntityId = (props.entity as { id?: string | number })?.id ?? null;
		if (!heldNotesBelongToCurrentEntity()) {
			// The held note was pushed for the entity we are LEAVING. Adopting it here would show (and on
			// the next save persist) one conversation's note under another — so it is dropped, but loudly:
			// this is the one discard in this function that genuinely loses a pushed value, and the entity
			// it belonged to is named so a "the note never appeared" report is traceable.
			console.warn("EntityAbout: dropping a held notes value on an entity change — it belongs to the outgoing entity", {
				heldForEntityId: pendingServerNotesEntityId,
				incomingEntityId,
				pendingLength: pending.length
			});
			return true;
		}
		if (pending === selectedNotes.value) {
			// Already showing it; nothing to move, and the caller rebaselines onto it either way.
			console.log("EntityAbout: held notes on entity change already match the field; nothing to adopt", {
				entityId: incomingEntityId,
				pendingLength: pending.length
			});
			return true;
		}
		console.log("EntityAbout: adopting notes held during an entity change", {
			entityId: incomingEntityId,
			pendingLength: pending.length,
			replacedLength: (selectedNotes.value || "").length
		});
		selectedNotes.value = pending;
		// Keep the entity prop in sync with the field for the same reason tryAdoptPendingServerNotes does:
		// otherwise the prop watcher sees entity.notes differ from a now-clean buffer and re-adopts the
		// stale value straight back. Guarded because a real switch blanks `entity` to undefined first
		// (EntityDetailPage.vue:2496) and this function also runs on that interlude.
		(props.entity as Conversation).notes = pending as any;
		return true;
	}

	// Rebaseline to the values a save actually persisted (its PUT-time snapshot), NOT the current live
	// values. Any edit made while the request was in flight then stays dirty instead of being silently
	// folded into a "clean" baseline — so it flows through the normal autosave/unsaved-changes machinery
	// (covers status changes, self-reassigns autosave can't do, etc.). The caller's finally re-arms.
	function commitSavedBaseline(signature: string, status: string, persistedNotes: string | null = null): void {
		initialSignature.value = signature;
		baselineStatus.value = status;
		// Only move the notes baseline when this save actually PUT a notes value. When it omitted `notes`
		// (user never edited them), the server's value — possibly a note pushed from the live call — stays
		// authoritative and the adoption watcher is still free to take it.
		if (persistedNotes !== null) {
			baselineNotes.value = persistedNotes;
			pendingServerNotes.value = null;
		}
		emitDirty(isFormDirty());
		// This save may have just made the form clean — take any note held during it.
		tryAdoptPendingServerNotes();
	}

	// Accept a programmatic (non-user) status change as clean. Only the status baseline moves, so a genuine
	// user edit to another field made during the auto-review window stays dirty and still prompts (LSP-16361).
	function acceptProgrammaticStatus(): void {
		baselineStatus.value = selectedStatus.value || "";
		const dirty = isFormDirty();
		emitDirty(dirty);
		// A programmatic status flip must not arm an autosave; re-evaluate against real dirtiness.
		maybeScheduleAutosave(dirty);
		// Review 4975642278 (medium): this is the third way the form can go clean, and it was the one that
		// did NOT adopt a held note — so the MOST COMMON live-call payload (notes AND status together,
		// e.g. the host saves notes as the recording advances to ReadyForReview) left the note stranded
		// forever. Ordering makes it unavoidable: the status watcher is registered first, so by the time
		// the notes watcher runs, selectedStatus has already moved off baselineStatus and isFormDirty() is
		// true — the note is held rather than adopted. This call then re-cleans the form, but no tracked
		// FIELD changed, so the deep watcher's `if (!dirty) tryAdoptPendingServerNotes()` never fires and
		// nothing else ever picks the note up: exactly this ticket's symptom. tryAdoptPendingServerNotes
		// re-checks dirtiness itself, so a genuine user edit still keeps the note held.
		tryAdoptPendingServerNotes();
	}

	// Keep this form's project buffer aligned when the mobile session header changes the assignment.
	// If the form was clean, accept the assignment into its baseline. If another field was already
	// edited, preserve that dirtiness while ensuring the eventual save sends the new project.
	function acceptProgrammaticProject(project: Project | null): void {
		const wasDirty = isFormDirty();
		selectedProject.value = project;
		if (!wasDirty) {
			captureBaselineSignature();
			cancelAutosave();
		}
		emitDirty(isFormDirty());
	}

	// Reset baseline when the entity changes
	watch(
		() => (props.entity as { id?: string | number })?.id,
		() => {
			resetDirtyBaseline();
			// Clear the "Last saved …" indicator so a previous entity's state doesn't carry onto this one.
			resetAutosave();
		},
		{ immediate: true }
	);

	// Mark form as dirty when any tracked field changes, but allow revert-to-original
	watch(
		[
			selectedUser,
			selectedCustomer,
			selectedProject,
			selectedTags,
			selectedNotes,
			selectedStatus,
			selectedAddress,
			() => (props.entity as { name?: string })?.name
		],
		() => {
			const dirty = isFormDirty();
			emitDirty(dirty);
			maybeScheduleAutosave(dirty);
			if (!dirty) {
				// The user reverted their edits — a note held while they were typing can land now.
				tryAdoptPendingServerNotes();
			}
		},
		{ deep: true }
	);

	// maybeScheduleAutosave only otherwise runs from the tracked-field watcher above (and
	// acceptProgrammaticStatus), which reads isValid as of the PREVIOUS keystroke — so correcting an
	// over-limit field back under its limit updates the field's own value (already handled above) but
	// isValid flips true on the FOLLOWING tick, after the field watcher already ran with the stale
	// (false) value and called cancelAutosave(). Nothing else re-arms it, so autosave silently
	// degrades to manual-only until the next tracked-field edit. Re-evaluate directly off isValid so a
	// validity-only flip (no other field change) still schedules/cancels correctly.
	watch(isValid, () => {
		maybeScheduleAutosave(isFormDirty());
	});

	// break-it round 2: originally added because nameRules (above) appended a project-only backstop
	// rule while entityType === "project" — Vuetify never re-runs a field's rules just because the
	// `rules` array a computed hands it changed reference, only an explicit validate() does, so a name
	// invalid under the OLD entityType's rules stayed invalid forever after switching entityType. That
	// specific backstop rule is gone (LSP-17482 — nameRules no longer varies by entityType), but this
	// re-validate is kept as a defensive no-op in case a future entityType-conditional rule reintroduces
	// the same staleness.
	watch(
		() => props.entityType,
		async () => {
			await nextTick();
			// Diagnostic (root CLAUDE.md's "Diagnostic logging when fixing silent failures"): if the
			// v-form ref is absent here, `form.value?.validate()` silently never runs, reintroducing the
			// exact stale-validation bug this watcher exists to fix (see the comment above) with no signal.
			if (!form.value) {
				console.warn("EntityAbout: entityType watcher fired but the form ref is not mounted — cannot re-validate", {
					entityId: (props.entity as { id?: string | number })?.id,
					entityType: props.entityType
				});
				return;
			}
			await form.value.validate();
		}
	);

	const showConversationStatus = computed<boolean>(() => {
		if (props.entityType !== "conversation") return false;
		const status = (props.entity as Conversation)?.status as string | undefined;
		return status === constants.CONVERSATION_STATUS.ReadyForReview || status === constants.CONVERSATION_STATUS.Reviewed;
	});

	// Self Service / Web Form conversations show a disabled "Started By" field
	const isSelfServiceStartedBy = computed<boolean>(() => {
		if (props.entityType !== "conversation") return false;
		const conv = props.entity as Conversation;
		const entityRecord = props.entity as unknown as Record<string, unknown>;
		const hasFormSubmission = !!(entityRecord as { FormSubmission?: unknown }).FormSubmission;
		return conv.initiationType === constants.INITIATION_TYPE.SELF || hasFormSubmission;
	});

	// Additional Information (Form Submission)
	const additionalFormFields = computed<FormSubmissionField[]>(() => {
		const submission = (props.entity as any)?.FormSubmission;
		const fields = submission?.FormSubmissionFields;
		return Array.isArray(fields) ? (fields as FormSubmissionField[]) : ([] as FormSubmissionField[]);
	});

	// LSP-19563: an Address field submitted through a V2 web form stores only the formatted text,
	// so the Maps link is a plain text search rather than a place-id lookup.
	function addressMapLink(field: FormSubmissionField): string | null {
		if (field.FormField?.type !== FieldType.Address) return null;
		const address = field.value?.trim();
		if (!address) return null;
		return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
	}

	const hasAdditionalInfo = computed<boolean>(() => {
		return (additionalFormFields.value?.length ?? 0) > 0;
	});

	async function onCreateCustomer(searchTerm: string, addItemCallback: any) {
		try {
			const record = await CreateCustomerDialogRef.value?.open?.(searchTerm || "");
			if (!record) return;
			addItemCallback(record as Customer);
			if (props.entityType === "project") {
				const arr = Array.isArray(selectedCustomer.value) ? (selectedCustomer.value as Customer[]) : [];
				// Dialog can resolve with an existing customer (Use existing path) — dedupe.
				if (!arr.some((c) => c.id === (record as Customer).id)) {
					arr.push(record as Customer);
				}
				selectedCustomer.value = arr;
			} else {
				selectedCustomer.value = record as Customer;
			}
			emit("update:entity", props.entity);
		} catch (err) {
			console.error(err);
		}
	}

	async function onEditCustomer(customerId: string, updateItemCallback: any) {
		try {
			const updated = await CreateCustomerDialogRef.value?.editContact?.(customerId);
			if (!updated) return;
			updateItemCallback(updated as Customer);
			if (props.entityType === "project") {
				const arr = Array.isArray(selectedCustomer.value) ? (selectedCustomer.value as Customer[]) : [];
				const idx = arr.findIndex((c) => c.id === (updated as Customer).id);
				if (idx !== -1) {
					arr.splice(idx, 1, updated as Customer);
					selectedCustomer.value = [...arr];
				}
			} else {
				selectedCustomer.value = updated as Customer;
			}
			emit("update:entity", props.entity);
		} catch (err) {
			console.error(err);
		}
	}

	// Shared success path for both entity types: stamp the indicator and, for a manual save only, toast.
	function announceSaved(isAutosave: boolean): void {
		markSaved();
		// Autosave is silent — the "Last saved …" indicator is the feedback. Manual Save keeps its toast.
		if (!isAutosave) {
			const message = "Your changes have been saved.";
			toast?.success?.(message, {
				timeout: calculateTimeout(message)
			});
		}
	}

	// Correlating identifiers stamped on every save() diagnostic below. Per CLAUDE.md's
	// silent-failure rule, EVERY early return in save() logs which condition fired and the state
	// that made it fire — a "my note vanished" report must be triageable from logs alone, without
	// reconstructing the gate from indirect signals (LSP-17265 review).
	function saveDiagnosticContext(isAutosave: boolean): Record<string, unknown> {
		return {
			entityId: (props.entity as { id?: string | number })?.id,
			entityType: props.entityType,
			isAutosave
		};
	}

	// Single source of truth for "a false save() result has already been accounted for", so
	// flushPendingSave never has to re-derive save()'s gate conditions (a duplicated predicate would go
	// stale the moment a gate is added, silently suppressing the flush warning).
	//
	// Passed in PER CALL rather than held in component scope: saves can overlap (the single-flight path
	// re-enters save(), and a manual Save can coalesce with a pending autosave), and a shared flag is
	// reset by whichever call starts last — wiping the first call's record and letting the caller stack a
	// second toast on a failure that was already reported. break-it round 2
	type SaveFailureReport = { alreadySurfaced: boolean };

	// Returns true when the entity was persisted and false when nothing was saved — an invalid form, a
	// blocked reassignment, a stale entity switch mid-PUT, or a caught server/offline error. The
	// unsaved-changes guard in EntityDetailPage relies on this: a false result keeps the user on the dirty
	// About tab so a failed/blocked save can't silently discard their edits (LSP-16279). Manual Save passes
	// no options (ignores the return value); autosave passes { isAutosave: true } to stay silent.
	async function save(options: { isAutosave?: boolean; failureReport?: SaveFailureReport } = {}): Promise<boolean> {
		// const toast = (instance?.proxy as any).$toast; // Removed in favor of useToast
		const isAutosave = options.isAutosave === true;
		// save() marks this only where a false return must NOT produce a caller-facing "we couldn't save"
		// toast: either save() already told the user, or the PUT did reach the server and only the local
		// result was discarded. flushPendingSave reads it to pick "failed" (caller stays quiet) over
		// "blocked" (caller warns). It stays false by default on purpose: a silent gate added here in
		// future then reports as "blocked", so the user is TOLD rather than losing the edit silently —
		// which is the entire point of the flush guard (LSP-17265).
		const failureReport = options.failureReport;

		// Re-check editability at fire time, not just when the debounce was armed: the page can become
		// view-only / free-trial-ended / unauthenticated during the 10s window, and a pending autosave
		// must not PUT after editing is locked. Manual save can't reach here while view-only (the Save
		// control is hidden), so this guard is scoped to the autosave path.
		if (isAutosave && (props.isViewOnly || props.hasFreeTrialEnded || !isAuthenticated.value)) {
			// Log WHICH of the three locked editing — they have very different follow-ups (permission
			// change vs expired trial vs dropped session).
			console.warn("EntityAbout.save: editing locked before the pending autosave fired — not saving", {
				...saveDiagnosticContext(isAutosave),
				isViewOnly: props.isViewOnly === true,
				hasFreeTrialEnded: props.hasFreeTrialEnded === true,
				isAuthenticated: isAuthenticated.value
			});
			cancelAutosave();
			return false;
		}

		if (!isValid.value) {
			// Diagnostic: this is a silent-failure gate for autosave (no toast below) — log every time
			// so a future "my note vanished" report doesn't need Datadog archaeology to reconstruct why.
			console.warn("EntityAbout.save: form invalid — not saving", saveDiagnosticContext(isAutosave));
			// Autosave stays quiet — the offending field is already showing its red error.
			if (!isAutosave) {
				toast.error(FIELDS_NEED_ATTENTION);
			}
			return false;
		}

		// Scheduled conversations exist because someone scheduled a session for an assignee —
		// they should never become orphaned. Other conversation types (inbound missed calls,
		// unassigned queue) legitimately have a null forcedUserId, so this gate is scoped to
		// the scheduled flow only.
		if (props.entityType === "conversation" && props.isScheduled && getNewAssigneeId() === null) {
			console.warn("EntityAbout.save: scheduled session has no assignee — not saving", {
				...saveDiagnosticContext(isAutosave),
				selectedUserId: (selectedUser.value as User | null)?.id ?? null,
				currentAssigneeId: getCurrentAssigneeId(props.entity)
			});
			if (!isAutosave) {
				toast.error("Oops. Scheduled sessions need an assignee. Please pick a user before saving.");
			}
			return false;
		}

		if (isReassigningAwayFromSelf()) {
			// Never pop the reassign confirmation from a background autosave — defer to an explicit Save click.
			if (isAutosave) {
				console.warn("EntityAbout.save: self-reassign needs an explicit Save — autosave deferred, not saving", {
					...saveDiagnosticContext(isAutosave),
					currentUserId: props.currentUserId ?? null,
					newAssigneeId: getNewAssigneeId()
				});
				return false;
			}
			if (showReassignConfirmDialog.value) {
				console.warn("EntityAbout.save: self-reassign confirmation is already open — not saving", {
					...saveDiagnosticContext(isAutosave),
					currentUserId: props.currentUserId ?? null,
					newAssigneeId: getNewAssigneeId()
				});
				return false;
			}

			const confirmed = await awaitReassignConfirmation();

			if (!confirmed) {
				console.warn("EntityAbout.save: self-reassign declined at the confirmation — not saving", {
					...saveDiagnosticContext(isAutosave),
					currentUserId: props.currentUserId ?? null,
					newAssigneeId: getNewAssigneeId()
				});
				return false;
			}
		}

		// Single-flight: never overlap two PUTs. If one is in flight, wait for it to settle, then run this
		// save for real. Returning the awaited persist (rather than resolving immediately) is essential —
		// promptSaveBeforeLeave awaits save() before clearing isDirty and navigating, so an early resolve
		// would let it drop edits made during the in-flight save (LSP-16420 / Bugbot).
		if (isPersisting) {
			try {
				await activeSavePromise;
			} catch {
				// In-flight save failed — fall through and still attempt this one.
			}
			return save(options);
		}

		// Field-value snapshot going into this PUT — used as the post-save baseline so edits made *during*
		// the in-flight request stay dirty (instead of being folded into a clean baseline and lost).
		const signatureBeforePut = buildDirtySignature();
		const statusBeforePut = selectedStatus.value || "";
		// PUT-time notes snapshot, for the same reason as the signature/status ones: a keystroke landing
		// while the request is in flight must stay dirty rather than be folded into the clean baseline.
		const notesBeforePut = selectedNotes.value || "";
		// The entity this save targets. If it changes during the PUT (navigation), the late response must
		// not apply entity A's outcome onto entity B.
		const savedEntityId = (props.entity as { id?: string | number })?.id;
		const isStillSameEntity = (): boolean => (props.entity as { id?: string | number })?.id === savedEntityId;
		let saved = false;

		// Expose this save's completion so a coalesced caller (above) can await the real persist.
		let resolveActiveSave!: () => void;
		activeSavePromise = new Promise<void>((resolve) => {
			resolveActiveSave = resolve;
		});

		try {
			isPersisting = true;
			markSaving();
			if (!isAutosave) {
				isSaving.value = true;
			}
			// Keep Labels in sync from local selection
			(props.entity as any).Labels = selectedTags.value as any;

			if (props.entityType === "conversation") {
				const conversation = props.entity as Conversation & Record<string, any>;
				// LSP-15744: capture the original assignee BEFORE we overwrite the conversation
				// payload, so we can detect when the current user is reassigning themselves away.
				// Admins keep read access via applyPermissions on the backend, so only non-admin
				// self-reassigners need the redirect (they'd otherwise sit on a 403/empty page).
				const previousAssigneeId = getCurrentAssigneeId(conversation);
				const newAssigneeId = getNewAssigneeId();
				const reassignedAwayFromSelf =
					!props.isAdmin &&
					!!props.currentUserId &&
					previousAssigneeId === props.currentUserId &&
					newAssigneeId !== props.currentUserId;
				// Selected team member
				conversation.selectedTeamMember = newAssigneeId;
				// Selected customer (single)
				const customer = (selectedCustomer.value as Customer | null) || null;
				conversation.workflowCustomerId = customer?.id ?? "";
				// Keep server-side linkage in sync explicitly on both set and clear
				conversation.newSelectedCustomer = customer;
				// Selected project (single)
				const selectedProj = (selectedProject.value as Project | null) || null;
				(conversation as any).projectId = selectedProj?.id ?? null;
				if (!selectedProj) {
					conversation.Project = null as any;
				}
				// LSP-16701 (Bugbot 3808760073 — DATA LOSS): only send `notes` when the user actually edited
				// them. Previously save() always sent `selectedNotes`, so an unrelated About save (tags,
				// assignee, …) shipped the notes value this form was seeded with and overwrote a note that
				// had arrived from the live call in the meantime — the adoption watcher deliberately refuses
				// to adopt while the form is dirty, so `selectedNotes` was stale by design. contact-api
				// applies the payload with Sequelize's `entity.update(incomingEntity)`, which only writes
				// keys PRESENT in the object, so omitting `notes` leaves the stored value untouched.
				const notesEdited = selectedNotes.value !== baselineNotes.value;
				const request = { ...conversation } as Record<string, any>;
				// Bugbot 3808841393: capture the conflict here (this is the value the PUT is about to
				// overwrite) but do NOT tell the user anything yet — the claim "your version was saved" may
				// only be made once the PUT has actually persisted. The toast is emitted after success below;
				// a failure is surfaced by the catch block's own error toast instead.
				let conflictingPushedNotes: string | null = null;
				if (notesEdited) {
					conversation.notes = selectedNotes.value;
					request.notes = selectedNotes.value;
					// The user edited notes AND a different value arrived from the call that they never saw.
					// Their typing must win (never silently discard what someone is composing), but the loss of
					// the pushed text must be visible rather than silent.
					// Review 4975055752 finding #1 — two questions, asked separately:
					//   `!== baselineNotes.value`  — did the SERVER value actually move since we last synced?
					//   `!== selectedNotes.value`  — is it actually different from the text we are shipping?
					// The watcher above already refuses to HOLD a value equal to the baseline, so the baseline
					// half here is defense-in-depth rather than the only gate (removing it alone keeps the specs
					// green). It is kept because this is the line that makes a user-facing claim: the condition
					// under which we tell someone their note was overwritten should read as its own statement,
					// not depend on a caller three hundred lines away maintaining the invariant.
					// Break-it round 2 adds the ownership half: a note held for a DIFFERENT entity is not a
					// conflict with this save, and claiming it is told the user their edit had overwritten a
					// live-call note that was never part of this conversation.
					if (
						pendingServerNotes.value !== null &&
						heldNotesBelongToCurrentEntity() &&
						pendingServerNotes.value !== baselineNotes.value &&
						pendingServerNotes.value !== selectedNotes.value
					) {
						conflictingPushedNotes = pendingServerNotes.value;
						// Diagnostic stays at detection time: this is the moment we know the PUT is about to
						// overwrite a pushed value, and it is the line worth having in triage even if the PUT
						// then fails. Worded as an attempt, not an outcome.
						console.warn(
							"EntityAbout.save: about to overwrite notes that changed during the call with the user's edited value",
							{
								conversationId: savedEntityId,
								pushedLength: conflictingPushedNotes.length,
								outgoingLength: selectedNotes.value.length
							}
						);
					}
				} else {
					// Never ship a notes value this form was not in sync with.
					delete request.notes;
				}
				// Status already bound via handlers
				const updated = await (api as any).updateConversation(conversation.id, request);
				// Entity switched mid-PUT — discard this stale result for the previous conversation.
				if (!isStillSameEntity()) {
					// The PUT itself succeeded, so nothing was lost server-side — but this returns false, and
					// silently. Log it so the false is attributable to navigation timing rather than a failure.
					console.warn("EntityAbout.save: entity switched mid-PUT — discarding the stale conversation result", {
						...saveDiagnosticContext(isAutosave),
						savedEntityId
					});
					// The PUT DID reach the server — only the local result is being dropped. Telling the user
					// "we couldn't save your last change" here would be false, so suppress the flush warning.
					if (failureReport) failureReport.alreadySurfaced = true;
					return false;
				}
				emit("update:entity", updated as Conversation);
				emit("save", updated as Conversation);
				// Review 4975055752 finding #3: `conflictingPushedNotes` was snapshotted BEFORE the await, so a
				// push that lands while the PUT is in flight moves pendingServerNotes past that snapshot — and
				// commitSavedBaseline (below) clears pendingServerNotes whenever this save actually PUT notes,
				// which dropped that fresher value with no adoption and no warning. Re-read it here, before the
				// clear, so the newest overwritten value is the one reported.
				if (notesEdited) {
					const pushedDuringPut = pendingServerNotes.value;
					// Break-it round 2: same ownership half as the pre-PUT check above — this is the second
					// place a conflict can be claimed, and guarding only the first still let a note held for a
					// DIFFERENT entity produce the user-facing "changed during the live call" toast.
					if (
						pushedDuringPut !== null &&
						heldNotesBelongToCurrentEntity() &&
						pushedDuringPut !== baselineNotes.value &&
						pushedDuringPut !== notesBeforePut
					) {
						if (pushedDuringPut !== conflictingPushedNotes) {
							console.warn(
								"EntityAbout.save: a newer notes value arrived mid-PUT and is being overwritten by the saved value",
								{
									conversationId: savedEntityId,
									pushedLength: pushedDuringPut.length,
									outgoingLength: notesBeforePut.length
								}
							);
						}
						conflictingPushedNotes = pushedDuringPut;
					}
				}
				commitSavedBaseline(signatureBeforePut, statusBeforePut, notesEdited ? notesBeforePut : null);
				// The PUT has landed, so this claim is now true (Bugbot 3808841393). Reached only on success;
				// on rejection the catch block's error toast tells the user nothing was saved, and
				// pendingServerNotes is left intact so a retry still detects the same conflict. The mid-PUT
				// entity-switch branch above returns before this point deliberately: that save did persist,
				// but a toast about a conversation the user has navigated away from is its own kind of
				// misleading — the console.warn above remains the trail for it.
				if (conflictingPushedNotes !== null) {
					// Review 4975642278 HIGH #1: this notification runs AFTER commitSavedBaseline and the
					// update:entity/save emits — i.e. after the write is fully persisted — but it used to be an
					// unguarded `toast.warning(...)` inside this try. On an instance with no toast plugin
					// (a real, previously-hit scenario: it is exactly why the catch below guards
					// `typeof toast?.error === "function"`), `toast.warning` is not a function, the TypeError
					// lands in the catch, and a save that DID persist is reported as a failure — markError(),
					// a "Failed to save changes" toast, and `false` returned to every caller that trusts that
					// boolean (the autosave flush and the unsaved-changes-before-leave guard). That is the
					// inverse of the bug this PR fixed: a cosmetic notification failure claiming data loss
					// that never happened. A post-persist notification can never fail the save.
					try {
						if (typeof toast?.warning === "function") {
							toast.warning("These notes also changed during the live call. Your version was saved — refresh to compare.", {
								timeout: 8000
							});
						} else {
							console.warn("EntityAbout.save: no toast.warning available to report the mid-PUT notes conflict", {
								...saveDiagnosticContext(isAutosave),
								savedEntityId
							});
						}
					} catch (notifyError) {
						console.warn("EntityAbout.save: the mid-PUT notes conflict notification threw; the save itself still persisted", {
							...saveDiagnosticContext(isAutosave),
							savedEntityId,
							notifyError
						});
					}
				}
				saved = true;
				announceSaved(isAutosave);
				if (reassignedAwayFromSelf) {
					emit("reassigned-away");
				}
			} else if (props.entityType === "project") {
				const project = props.entity as Project & Record<string, any>;
				// Customers (multiple supported in UI)
				const customersArray: Customer[] = Array.isArray(selectedCustomer.value)
					? (selectedCustomer.value as Customer[]) || []
					: ((selectedCustomer.value ? [selectedCustomer.value as Customer] : []) as Customer[]);
				project.Customers = customersArray;
				// Primary customer (align with ProjectPage behavior)
				const primaryCustomer: Customer | null = Array.isArray(selectedCustomer.value)
					? ((selectedCustomer.value as Customer[])[0] ?? null)
					: ((selectedCustomer.value as Customer) ?? null);
				project.newSelectedCustomer = primaryCustomer;
				// Selected address
				project.GeoAddress = selectedAddress.value as GeoAddress | null;
				if (!project.GeoAddress) {
					// Seems wrong, but only way to actually clear the address from the project.
					project.addressId = null;
				}
				// Selected team member (user)
				project.selectedTeamMember = (selectedUser.value as User | null)?.id ?? project.selectedTeamMember ?? null;
				const request = { ...project };
				const updated = (await (api as any).updateProject(project.id, request)) as Project & Record<string, any>;
				// Entity switched mid-PUT — discard this stale result for the previous project.
				if (!isStillSameEntity()) {
					// As above: the PUT landed; the false is a navigation-timing discard, not a save failure.
					console.warn("EntityAbout.save: entity switched mid-PUT — discarding the stale project result", {
						...saveDiagnosticContext(isAutosave),
						savedEntityId
					});
					// See the conversation branch above: the PUT reached the server, so no caller toast.
					if (failureReport) failureReport.alreadySurfaced = true;
					return false;
				}
				// LSP-16551: make the adopted project authoritative for contacts. The parent replaces its
				// in-memory project with this emitted entity and propagates it to children — including the
				// ProjectActionFAB's `:project` prop, which the Action* dialogs read to prefill the current
				// first contact. PUT /projects/:id echoes the refreshed `Customers` association, so prefer it;
				// but if a response ever omits it, fall back to the contacts the user just saved rather than
				// emitting a project with a staler (or empty) `Customers` than what was persisted — otherwise
				// a removed contact could linger, or a real contact could vanish from every consumer.
				if (updated && !Array.isArray(updated.Customers)) {
					updated.Customers = customersArray;
				}
				emit("update:entity", updated as Project);
				emit("save", updated as Project);
				commitSavedBaseline(signatureBeforePut, statusBeforePut);
				saved = true;
				announceSaved(isAutosave);
			}

			// conversation/project success → saved=true. Any other entityType has no persist path: fail
			// closed (return false) so the unsaved-changes guard keeps the user on the page rather than
			// silently reporting a success that never persisted anything (LSP-16279).
			if (!saved) {
				console.warn("EntityAbout.save: no persist path for entityType — nothing saved", saveDiagnosticContext(isAutosave));
			}
			return saved;
		} catch (e: any) {
			// On failure tell the user clearly and don't let the indicator claim it saved — but only for the
			// entity still on screen; a stale failure from a navigated-away entity stays silent.
			if (isStillSameEntity()) {
				markError();
				const message = e?.message ?? "Failed to save changes.";
				// Mark the failure surfaced ONLY if a toast was actually shown. The optional chaining below
				// can silently no-op (no toast plugin on this instance), and claiming "already surfaced"
				// then makes flushPendingSave report "failed" — so the caller stays quiet too and NOBODY
				// tells the user their edit was lost. break-it round 4
				if (typeof toast?.error === "function") {
					toast.error(message, {
						timeout: calculateTimeout(message)
					});
					if (failureReport) failureReport.alreadySurfaced = true;
				}
			}
			console.error(e);
			// LSP-16279: the save failed (server/offline error). Signal failure so the unsaved-changes guard
			// keeps the user on the page to see this toast and retry, instead of silently discarding edits.
			return false;
		} finally {
			isPersisting = false;
			if (!isAutosave) {
				isSaving.value = false;
			}
			activeSavePromise = null;
			// Re-arm the debounced autosave for edits that landed *during* this PUT (they only changed
			// fields; they didn't call save()). After a successful save the baseline reflects what was
			// persisted, so isFormDirty() is true exactly when such edits remain. A coalesced save() does
			// NOT need handling here — it awaits this promise and runs itself. Skip if this instance
			// unmounted or the entity was navigated away from (a fresh EntityAbout owns the new entity).
			if (saved && !isUnmounted && isStillSameEntity()) {
				if (isFormDirty()) {
					scheduleAutosave();
				} else {
					cancelAutosave();
				}
			}
			// Let any coalesced caller proceed now that this save has fully settled.
			resolveActiveSave();
		}
	}

	function onProjectStatusInput(v: string) {
		selectedStatus.value = v;
		(props.entity as Project).status = v;
		emit("update:entity", props.entity);
	}

	function onConversationStatusInput(v: string) {
		selectedStatus.value = v;
		(props.entity as Conversation).status = v;
		emit("update:entity", props.entity);
	}

	function onPlaceChanged(place: GeoAddress | null) {
		(props.entity as any).GeoAddress = place;
		emit("update:entity", props.entity);
	}

	function onGeoAddressSelected(geoAddress: GeoAddress) {
		(props.entity as any).GeoAddress = {
			latitude: geoAddress.latitude,
			longitude: geoAddress.longitude,
			placeId: geoAddress.placeId,
			formattedAddress: geoAddress.formattedAddress
		} as any;
		selectedAddress.value = geoAddress;
		emit("update:entity", props.entity);
	}

	function openMapsApp() {
		if (!(props.entity as any).GeoAddress?.formattedAddress) {
			return;
		}

		const encodedAddress = encodeURIComponent((props.entity as any).GeoAddress.formattedAddress);

		if (DeviceHelper.isIOS || DeviceHelper.isIpad) {
			window.location.href = `maps://?q=${encodedAddress}`;
		} else if (DeviceHelper.isAndroid || DeviceHelper.isTablet) {
			window.location.href = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
		} else {
			console.warn("Unsupported device for address navigation.");
		}
	}

	/* Properties exposed out to our children :D */
	// Persist any pending (debounced) autosave immediately, used when the bound entity is about to be
	// swapped by an in-place navigation (same-route :id change) — which does NOT fire onBeforeRouteLeave.
	// Without this, the outgoing entity's debounced edit would be dropped when the component is torn down
	// (LSP-16420 "never lose work").
	//
	// A bare boolean is not enough for the caller: save() is silent for SOME failures and toasts for
	// others, so a single "false" cannot tell the caller whether the user still needs to be told
	// (LSP-17265 review). The three outcomes:
	//   "saved"   — nothing to persist (clean form), or the persist succeeded.
	//   "blocked" — nothing persisted AND save() deliberately raised no toast (its silent gates:
	//               invalid form, editing locked mid-window, scheduled session missing an assignee,
	//               self-reassign that needs an explicit Save click). The caller MUST speak for these:
	//               the offending field is about to unmount, so its inline red error is useless.
	//   "failed"  — nothing persisted and save() already surfaced its own error toast (server/offline).
	//               The caller must stay quiet or the user gets two toasts for one failure.
	type FlushPendingSaveResult = "saved" | "blocked" | "failed";

	async function flushPendingSave(): Promise<FlushPendingSaveResult> {
		cancelAutosave();
		if (!isFormDirty()) {
			return "saved";
		}
		// Ask save() what happened rather than re-deriving its gates here. The report token is per call,
		// so an overlapping/coalesced save can't reset our record (break-it round 2). save() marks it only
		// where a false result is already accounted for (it toasted the user, or the PUT reached the server
		// and just the local result was dropped). Everything else — including any silent gate added to
		// save() later — reports as "blocked" so the caller warns instead of letting the edit disappear
		// (LSP-17265).
		const failureReport: SaveFailureReport = { alreadySurfaced: false };
		const persisted = await save({ isAutosave: true, failureReport });
		if (persisted) {
			return "saved";
		}
		return failureReport.alreadySurfaced ? "failed" : "blocked";
	}

	// Why a save is currently impossible, in words the user can act on — or null when it's fine.
	// EntityDetailPage needs this to offer "Discard / Keep editing" instead of a Save button that
	// cannot succeed (Jake: hitting Save "complains it couldn't and goes no where").
	const blockingValidationReason = computed<string | null>(() => {
		if (isValid.value !== false) return null;
		const noteLength = countCodePoints(selectedNotes.value ?? "");
		if (noteLength > NOTES_MAX_LENGTH) {
			return `the note is ${noteLength.toLocaleString()} characters, over the ${NOTES_MAX_LENGTH.toLocaleString()} limit`;
		}
		return "some fields need corrections";
	});

	defineExpose({
		save,
		flushPendingSave,
		isValid,
		blockingValidationReason,
		isSaving,
		selectedUser,
		selectedStatus,
		showReassignConfirmDialog,
		onReassignConfirm,
		onReassignCancel,
		acceptProgrammaticStatus,
		acceptProgrammaticProject,
		// LSP-16658: a full re-baseline for a caller that has just replaced `entity` with a server
		// response it knows the user did not produce. Safe only because the reschedule path refuses to
		// run at all while this form is dirty, so there is never a user edit here to swallow.
		rebaselineFromEntity: resetDirtyBaseline
	});
</script>
<style>
	.hidden-chevron {
		display: none !important;
	}
</style>
